import { addresseeLabels, type AddresseeUnit } from '@/lib/addressee';
import { cn } from '@/lib/utils';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ [D4.4 §1b] WHO A REQUIREMENT OR ALLOCATION ROW IS ADDRESSED TO — `CD · H`.
 *
 *  Every place a requirement or allocation row renders now says which DIVISION it is
 *  addressed to, and the UNIT where one is named. Before this the fill sheet printed the
 *  unit's name only when a line named one, so the ordinary division-only allocation — the
 *  common case — rendered as nothing at all, and a nodal reading a shared order could not
 *  tell whose people were being asked for.
 *
 *  ⚠️ [D4.5 §1a] IT IS READ-ONLY, AND IT NO LONGER HAS AN INTERACTIVE VARIANT.
 *
 *  It used to take an `onOpen` and render a `<button>` wrapped in an AntD `Tooltip` when one
 *  was given — the force grid's source cell, which collapsed its two pickers into these
 *  codes and reopened them on a click. §1a reverses that collapse (the collapsed text gave
 *  no hint it was editable), and `SourceCell` was the ONLY consumer of the interactive
 *  branch. §9 forbids keeping a branch with no call site, so it went with it, and
 *  `e2e/fixtures/overlays.ts`'s exemption for the Tooltip it mounted went in the same commit
 *  — an exemption for a site that no longer exists fails `census.spec.ts` just as loudly as
 *  an unswept overlay.
 *
 *  What survives is every surface where there is genuinely nothing to open: the fill sheet,
 *  the nodal inbox and the printed order preview.
 *
 *  ⚠️ THE FULL NAMES ARE IN THE ACCESSIBLE TEXT, NOT ONLY IN A TOOLTIP. Hover is
 *  unavailable on touch and unreachable from the keyboard, and a short code is meaningless
 *  to anyone who has not learned the establishment — so hover is the convenience and the
 *  accessible text is the channel.
 *
 *  ⚠️ A NATIVE `title`, NOT AntD's `Tooltip`, AND THAT IS MEASURED. This renders once per
 *  allocation row: on the fill sheet, on every inbox row, and once per source in the order
 *  preview — which is class-hidden but still RENDERED. An AntD `Tooltip` mounts an
 *  rc-trigger and a `ResizeObserver` per instance, and wrapping every one of those rows in
 *  one **doubled the arrangement builder's test file from 64s to 119s** and pushed three
 *  tests past the default timeout. A `title` attribute gives the same hover for free, and
 *  the `sr-only` name is what actually carries the meaning.
 * ─────────────────────────────────────────────────────────────────────────────────
 */

export type AddresseeCodesProps = {
  division: AddresseeUnit | null;
  /** ⚠️ [D4.5 §3d] An ARRAY is one inbox row — see `lib/addressee.ts`. `null` inside it is `Any`. */
  unit: AddresseeUnit | null | readonly (AddresseeUnit | null)[];
  className?: string;
  testId?: string;
};

const SHAPE = 'truncate rounded-md px-1.5 py-1 text-left font-mono text-xs';

export const AddresseeCodes = ({ division, unit, className, testId }: AddresseeCodesProps) => {
  const { codes, names, path } = addresseeLabels(division, unit);

  /* ⚠️ `aria-label` ON A `<span>` IS NOT HONOURED WITHOUT A ROLE, so the names go in the text
     layer instead: the codes are shown and the names are read out. */
  return (
    /* ⚠️ [UX1 §1e] THE HOVER IS THE PATH AND THE SPOKEN TEXT IS THE NAMES — two forms, two
       channels, one derivation. See `addresseeLabels`; `>` reads aloud as "greater than". */
    <span data-testid={testId} title={path} className={cn(SHAPE, 'inline-block', className)}>
      <span aria-hidden>{codes}</span>
      <span className="sr-only">{names}</span>
    </span>
  );
};
